import { isValidElement } from "react";

import type { DropDownProps } from "antd/lib/dropdown";
import { useTracking } from "react-tracking";
import { useMediaQuery } from "usehooks-ts";

import { BuiMenu } from "@btnet/blueprint-components/BuiMenu";
import {
  BuiSplitButton,
  type BuiSplitButtonMenuEntry,
  isBuiSplitButtonGroup,
} from "@btnet/blueprint-components/BuiSplitButton";
import { BuiTokenScreenMd } from "@btnet/blueprint-design-tokens";

import type { BTButtonType } from "../BTButton/BTButton";
import type { BTDropdownMenuItem } from "../BTDropdownButton/BTDropdownButton";
import { Hotkey } from "../Hotkey/Hotkey";
import type {
  IHotkeyWithPopoverConfig,
  THotkeyCommands,
} from "../Hotkey/hotkey.types";
import { getHotkeyProps } from "../Hotkey/hotkey.utility";

/** A single action for BTSplitButton (the primary button or a menu item). */
export type BTSplitButtonItem = BTDropdownMenuItem & {
  /**
   * Hotkey (or hotkey-with-popover config) for the action. When set, pressing the key
   * triggers `onClick` and the hint renders in a popover. Hotkeys are bundled directly
   * in this `BT*` layer — the decorative `BuiSplitButton` is unaware of them.
   */
  hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;
  icon?: React.ReactNode;
};

export interface IBTSplitButtonProps<ActionType> {
  "data-testid": string;
  actionBeingPerformed?: ActionType;
  loadingAction?: ActionType;
  /**
   * The primary action rendered as the left button. Its `content` is the label and its
   * `onClick` fires when the main button is clicked.
   */
  buttonAction: BTSplitButtonItem;
  type?: BTButtonType;
  /**
   * Secondary actions shown in the caret dropdown. If omitted, empty, or all-disabled,
   * no dropdown is rendered and only the primary button is shown. An entry with `items`
   * renders as a titled section of those items.
   */
  menuItems?: BuiSplitButtonMenuEntry<BTSplitButtonItem>[];
  className?: string;
  /**
   * Disables both the primary button and the dropdown trigger.
   * @default false
   */
  disabled?: boolean;
  /**
   * Placement of the dropdown menu (antd placement), passed through to the dropdown.
   * @default "bottomRight"
   */
  placement?: DropDownProps["placement"];
  /**
   * When true, the primary button collapses to its icon at the medium breakpoint
   * (768px) when it has an icon.
   * @default false
   */
  collapse?: boolean;
  /**
   * Pure passthrough to the underlying dropdown for popup containment (e.g. closest
   * modal). Supplied by the consuming app — this package holds no app code.
   */
  getPopupContainer?: DropDownProps["getPopupContainer"];
  /**
   * Passthrough to the dropdown: when `false`, focus is not returned to the caret trigger on
   * menu close (focus-into-menu on open and Escape-to-close still work). Set to `false` when a
   * menu action swaps in a focus-owning element the close focus-return would otherwise steal
   * focus from.
   * @default true
   */
  returnFocusOnClose?: boolean;
}

/**
 * Resolves loading/disabled to match the legacy getLoadingAction semantics
 * (Clients.App/src/utilities/form/form.ts — app code this shared package can't import,
 * so the logic is replicated here). `loading` = the MATCHING action is in flight (shows
 * the spinner). A DIFFERENT action in flight leaves loading false but still disables
 * both buttons — matching isPerformingAction. Without that branch, other split-button
 * actions stayed clickable mid-save and could fire concurrent mutations.
 */
function resolveSplitButtonState<ActionType>(
  props: IBTSplitButtonProps<ActionType>,
): { loading: boolean; resolvedDisabled: boolean } {
  const { actionBeingPerformed, loadingAction, disabled } = props;
  const hasLoadingConfig =
    "actionBeingPerformed" in props &&
    actionBeingPerformed !== undefined &&
    loadingAction !== undefined;
  const loading = hasLoadingConfig && actionBeingPerformed === loadingAction;
  const resolvedDisabled =
    (disabled ?? false) ||
    loading ||
    (hasLoadingConfig && actionBeingPerformed !== loadingAction);
  return { loading, resolvedDisabled };
}

/**
 * Narrows a node to a `<BTSplitButton>` element, so a caller holding opaque `ReactNode` content
 * (such as `BTOverflow`, which receives whatever an item wraps) can read its typed props.
 * Matches only an element whose type is `BTSplitButton` itself; a split button wrapped in another
 * element or component is not recognised.
 * @param node - The node to test.
 */
export function isBTSplitButtonElement(
  node: React.ReactNode,
): node is React.ReactElement<
  IBTSplitButtonProps<unknown>,
  typeof BTSplitButton
> {
  return isValidElement(node) && node.type === BTSplitButton;
}

/**
 * Renders a BTSplitButton as sibling `BuiMenu.Item` rows: the primary action first, then each
 * caret menu item. Used by `BTOverflow` when a split button rolls into the "…" menu, where a
 * split button nested inside one menu row would have to share that row between two click
 * targets. Disabled/loading state and analytics match the inline split button.
 * @param props - The same props the inline `BTSplitButton` received.
 */
export function BTSplitButtonOverflowItems<ActionType>(
  props: IBTSplitButtonProps<ActionType>,
): React.ReactElement {
  const { "data-testid": testid, buttonAction, menuItems } = props;
  const { trackEvent } = useTracking<Record<string, unknown>>({
    element: "Split Button",
    uniqueId: testid,
  });
  const { resolvedDisabled } = resolveSplitButtonState(props);

  const rows: { item: BTSplitButtonItem; event: string }[] = [
    { item: buttonAction, event: "ButtonClick" },
    ...(menuItems ?? [])
      .flatMap((x) => (isBuiSplitButtonGroup(x) ? x.items : [x]))
      .map((item) => ({ item, event: "MenuItemClick" })),
  ];

  return (
    <>
      {rows.map(({ item, event }) => (
        // BuiMenu.Item, not BTMenu.Item: BTMenu.Item fires its own MenuItemClick, which would double
        // the Split Button events below. The inline split button renders the undecorated item too.
        <BuiMenu.Item
          key={item["data-testid"]}
          // rc-menu's identity for the row. The `key` above never reaches it because these rows
          // render through a wrapper component rather than as direct children of the menu, so
          // without an explicit one every row shares an id and highlights together.
          eventKey={`${testid}-${item["data-testid"]}`}
          testid={item["data-testid"]}
          disabled={resolvedDisabled || (item.disabled ?? false)}
          {...(item["aria-label"] ? { "aria-label": item["aria-label"] } : {})}
          onClick={() => {
            try {
              return item.onClick();
            } finally {
              trackEvent({ event });
            }
          }}
        >
          {item.icon}
          {item.content}
        </BuiMenu.Item>
      ))}
    </>
  );
}

/**
 * BTSplitButton - the business-aware split button wrapping the decorative
 * `BuiSplitButton`.
 *
 * This `BT*` layer bundles the BTNet-specific concerns directly (the correct home for
 * them): analytics via the `react-tracking` peer dependency, and hotkeys via the core
 * `Hotkey` component. The decorative `BuiSplitButton` owns layout, the caret menu, and
 * keyboard/focus behavior.
 *
 * Behavior parity with the legacy wrapper:
 * - `actionBeingPerformed`/`loadingAction` resolve to the button loading/disabled state.
 * - A menu item with a `hotkey` triggers its `onClick` on keypress and renders the hint.
 * - When `menuItems` is absent, empty, or all-disabled, renders a standalone button.
 * @param props - The component props.
 * @example
 * ```tsx
 * <BTSplitButton
 *   data-testid="save-split"
 *   buttonAction={{ "data-testid": "save", content: "Save", onClick: handleSave, hotkey: "save" }}
 *   menuItems={[
 *     { "data-testid": "save-new", content: "Save and new", onClick: handleSaveNew },
 *   ]}
 * />
 * ```
 */
export function BTSplitButton<ActionType>(
  props: IBTSplitButtonProps<ActionType>,
): React.ReactElement {
  const {
    "data-testid": testid,
    buttonAction,
    menuItems,
    type = "secondary",
    className,
    placement,
    collapse,
    getPopupContainer,
    returnFocusOnClose,
  } = props;

  const { Track, trackEvent } = useTracking<Record<string, unknown>>({
    element: "Split Button",
    uniqueId: testid,
  });

  const mdBreakpoint = useMediaQuery(`(max-width: ${BuiTokenScreenMd})`);

  const { loading, resolvedDisabled } = resolveSplitButtonState(props);

  // Bundle analytics directly in this BT layer: the decorative BuiButton / BuiMenu.Item
  // fire nothing. Emit the same events the legacy BTButton (`ButtonClick`) and
  // BTMenu.Item (`MenuItemClick`) did, firing right after invoking onClick (NOT awaited),
  // so a handler that navigates away or rejects doesn't swallow the event.
  const withAnalytics = (
    item: BTSplitButtonItem,
    event: string,
  ): BTSplitButtonItem => ({
    ...item,
    onClick: () => {
      try {
        return item.onClick();
      } finally {
        trackEvent({ event });
      }
    },
  });

  // Wrap a menu item's content in <Hotkey>. onCommand calls the item's RAW onClick
  // (captured before analytics wrapping below) — matching legacy, where a hotkey press
  // fired the Hotkey's own event but NOT MenuItemClick; only a real click did.
  const withHotkey = (item: BTSplitButtonItem): BTSplitButtonItem =>
    item.hotkey
      ? {
          ...item,
          content: (
            <Hotkey
              {...getHotkeyProps(item.hotkey)}
              disabled={item.disabled ?? false}
              onCommand={async () => {
                if (!item.disabled) {
                  await item.onClick();
                }
              }}
            >
              {item.content}
            </Hotkey>
          ),
        }
      : item;

  // Order matters: withHotkey first so its onCommand closes over the raw onClick, then
  // withAnalytics wraps only the click-path onClick. A real click fires MenuItemClick; a
  // hotkey press does not (legacy parity).
  const enrichItem = (x: BTSplitButtonItem) =>
    withAnalytics(withHotkey(x), "MenuItemClick");
  const enrichedMenuItems = menuItems?.map((x) =>
    isBuiSplitButtonGroup(x)
      ? { ...x, items: x.items.map(enrichItem) }
      : enrichItem(x),
  );
  const enrichedButtonAction = withAnalytics(buttonAction, "ButtonClick");

  const splitButton = (
    <BuiSplitButton
      data-testid={testid}
      buttonAction={enrichedButtonAction}
      {...(enrichedMenuItems ? { menuItems: enrichedMenuItems } : {})}
      type={type}
      loading={loading}
      disabled={resolvedDisabled}
      collapse={(collapse ?? false) && mdBreakpoint}
      {...(className ? { className } : {})}
      {...(placement ? { placement } : {})}
      {...(getPopupContainer ? { getPopupContainer } : {})}
      {...(returnFocusOnClose !== undefined ? { returnFocusOnClose } : {})}
    />
  );

  // The primary action's hotkey wraps the whole split button rather than the primary
  // button's content — this keeps the keybinding registered even when the primary button
  // collapses to icon-only (its label, and any content-nested Hotkey, is unmounted).
  // Unlike menu-item hotkeys, the primary hotkey fires ButtonClick (legacy parity:
  // BTButton's hotkey path ran the same handleClick a real click did).
  const content = buttonAction.hotkey ? (
    <Hotkey
      {...getHotkeyProps(buttonAction.hotkey)}
      disabled={resolvedDisabled}
      onCommand={async () => {
        if (!resolvedDisabled) {
          await enrichedButtonAction.onClick();
        }
      }}
    >
      {splitButton}
    </Hotkey>
  ) : (
    splitButton
  );

  return <Track>{content}</Track>;
}
